import React, { useEffect, useMemo, useState } from 'react';
import { Calendar1, ListFilter } from 'lucide-react';
import { formatNumber } from '../utils/formatters';
import './ServiceHistoryModal.css';

function formatCounterCell(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return formatNumber(n);
}

function nullableToBlank(val) {
  if (val == null) return '';
  const s = String(val).trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  return s;
}

const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

const ServiceHistoryModal = ({
  open,
  onClose,
  serie,
  patrimonio,
  modelo
}) => {
  const [activeTab, setActiveTab] = useState('movimentacao');
  const [movimentacao, setMovimentacao] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [pecas, setPecas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedMotivo, setExpandedMotivo] = useState(null);
  const [expandedLaudo, setExpandedLaudo] = useState(null);

  const [showTipoFilter, setShowTipoFilter] = useState(false);
  const [showAberturaFilter, setShowAberturaFilter] = useState(false);
  const [showClienteFilter, setShowClienteFilter] = useState(false);
  const [showTecnicoFilter, setShowTecnicoFilter] = useState(false);
  const [showFechamentoFilter, setShowFechamentoFilter] = useState(false);
  const [tipoSearchText, setTipoSearchText] = useState('');
  const [clienteSearchText, setClienteSearchText] = useState('');
  const [tecnicoSearchText, setTecnicoSearchText] = useState('');
  const [selectedTipos, setSelectedTipos] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [selectedTecnicos, setSelectedTecnicos] = useState([]);
  const [aberturaStart, setAberturaStart] = useState('');
  const [aberturaEnd, setAberturaEnd] = useState('');
  const [fechamentoStart, setFechamentoStart] = useState('');
  const [fechamentoEnd, setFechamentoEnd] = useState('');

  const [showPecasDataFilter, setShowPecasDataFilter] = useState(false);
  const [showReferenciaFilter, setShowReferenciaFilter] = useState(false);
  const [showProdutoFilter, setShowProdutoFilter] = useState(false);
  const [pecasDataStart, setPecasDataStart] = useState('');
  const [pecasDataEnd, setPecasDataEnd] = useState('');
  const [referenciaSearchText, setReferenciaSearchText] = useState('');
  const [produtoSearchText, setProdutoSearchText] = useState('');
  const [selectedReferencias, setSelectedReferencias] = useState([]);
  const [selectedProdutos, setSelectedProdutos] = useState([]);

  const headerSubtitle = useMemo(() => {
    return `Série: ${serie || '-'} • Patrimônio: ${patrimonio || '-'}`;
  }, [serie, patrimonio]);

  const uniqueTipos = useMemo(() => [...new Set(ordens.map((o) => o.TIPO_OS || '').filter(Boolean))].sort(), [ordens]);
  const uniqueClientes = useMemo(() => [...new Set(ordens.map((o) => o.TB01008_NOME || '').filter(Boolean))].sort(), [ordens]);
  const uniqueTecnicos = useMemo(() => [...new Set(ordens.map((o) => o.TB01024_NOME || '').filter(Boolean))].sort(), [ordens]);
  const uniqueReferencias = useMemo(() => [...new Set(pecas.map((p) => p.TB01010_REFERENCIA || '').filter(Boolean))].sort(), [pecas]);
  const uniqueProdutos = useMemo(() => [...new Set(pecas.map((p) => p.TB01010_NOME || '').filter(Boolean))].sort(), [pecas]);

  useEffect(() => setSelectedTipos(uniqueTipos), [uniqueTipos]);
  useEffect(() => setSelectedClientes(uniqueClientes), [uniqueClientes]);
  useEffect(() => setSelectedTecnicos(uniqueTecnicos), [uniqueTecnicos]);
  useEffect(() => setSelectedReferencias(uniqueReferencias), [uniqueReferencias]);
  useEffect(() => setSelectedProdutos(uniqueProdutos), [uniqueProdutos]);

  const filteredTiposList = useMemo(
    () => uniqueTipos.filter((t) => t.toLowerCase().includes(tipoSearchText.toLowerCase())),
    [uniqueTipos, tipoSearchText]
  );
  const filteredClientesList = useMemo(
    () => uniqueClientes.filter((c) => c.toLowerCase().includes(clienteSearchText.toLowerCase())),
    [uniqueClientes, clienteSearchText]
  );
  const filteredTecnicosList = useMemo(
    () => uniqueTecnicos.filter((t) => t.toLowerCase().includes(tecnicoSearchText.toLowerCase())),
    [uniqueTecnicos, tecnicoSearchText]
  );
  const filteredReferenciasList = useMemo(
    () => uniqueReferencias.filter((r) => r.toLowerCase().includes(referenciaSearchText.toLowerCase())),
    [uniqueReferencias, referenciaSearchText]
  );
  const filteredProdutosList = useMemo(
    () => uniqueProdutos.filter((p) => p.toLowerCase().includes(produtoSearchText.toLowerCase())),
    [uniqueProdutos, produtoSearchText]
  );

  const filteredOrdens = useMemo(() => {
    return ordens.filter((o) => {
      if (selectedTipos.length > 0 && o.TIPO_OS && !selectedTipos.includes(o.TIPO_OS)) return false;
      if (selectedClientes.length > 0 && o.TB01008_NOME && !selectedClientes.includes(o.TB01008_NOME)) return false;
      if (selectedTecnicos.length > 0 && o.TB01024_NOME && !selectedTecnicos.includes(o.TB01024_NOME)) return false;

      if (aberturaStart && aberturaEnd && o.TB02115_DATA) {
        const d = new Date(String(o.TB02115_DATA).split(' ')[0].split('/').reverse().join('-'));
        const s = new Date(aberturaStart);
        const e = new Date(aberturaEnd);
        e.setHours(23, 59, 59, 999);
        if (Number.isFinite(d.getTime()) && (d < s || d > e)) return false;
      }

      if (fechamentoStart && fechamentoEnd && o.TB02122_DATAFIM) {
        const d = new Date(String(o.TB02122_DATAFIM).split(' ')[0].split('/').reverse().join('-'));
        const s = new Date(fechamentoStart);
        const e = new Date(fechamentoEnd);
        e.setHours(23, 59, 59, 999);
        if (Number.isFinite(d.getTime()) && (d < s || d > e)) return false;
      }
      return true;
    });
  }, [
    ordens,
    selectedTipos,
    selectedClientes,
    selectedTecnicos,
    aberturaStart,
    aberturaEnd,
    fechamentoStart,
    fechamentoEnd
  ]);

  const filteredPecas = useMemo(() => {
    return pecas.filter((p) => {
      if (selectedReferencias.length > 0 && p.TB01010_REFERENCIA && !selectedReferencias.includes(p.TB01010_REFERENCIA)) return false;
      if (selectedProdutos.length > 0 && p.TB01010_NOME && !selectedProdutos.includes(p.TB01010_NOME)) return false;
      if (pecasDataStart && pecasDataEnd && p.TB02021_DATA) {
        const d = new Date(String(p.TB02021_DATA).split(' ')[0].split('/').reverse().join('-'));
        const s = new Date(pecasDataStart);
        const e = new Date(pecasDataEnd);
        e.setHours(23, 59, 59, 999);
        if (Number.isFinite(d.getTime()) && (d < s || d > e)) return false;
      }
      return true;
    });
  }, [pecas, selectedReferencias, selectedProdutos, pecasDataStart, pecasDataEnd]);

  useEffect(() => {
    if (!open || !serie) return;
    const fetchTabData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const endpoint =
          activeTab === 'movimentacao'
            ? '/api/oficina/movimentacao'
            : activeTab === 'ordens'
            ? '/api/oficina/ordens-serie'
            : '/api/oficina/pecas-trocadas';

        const response = await fetch(`${API_BASE_URL}${endpoint}?serie=${encodeURIComponent(serie)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (activeTab === 'movimentacao') setMovimentacao([]);
          if (activeTab === 'ordens') setOrdens([]);
          if (activeTab === 'pecas') setPecas([]);
          return;
        }

        const data = await response.json();
        const list = data?.success && Array.isArray(data?.data) ? data.data : [];
        if (activeTab === 'movimentacao') setMovimentacao(list);
        if (activeTab === 'ordens') setOrdens(list);
        if (activeTab === 'pecas') setPecas(list);
      } catch (error) {
        console.error('Erro ao carregar histórico de serviço:', error);
        if (activeTab === 'movimentacao') setMovimentacao([]);
        if (activeTab === 'ordens') setOrdens([]);
        if (activeTab === 'pecas') setPecas([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTabData();
  }, [open, activeTab, serie]);

  if (!open) return null;

  return (
    <div className="service-history-overlay" onClick={onClose}>
      <div className={`service-history-modal modal-history ${activeTab === 'ordens' ? 'modal-history-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="service-history-header modal-history-header">
          <div>
            <h3 className="service-history-title modal-title">{modelo || 'Histórico de serviço'}</h3>
            <div className="service-history-subtitle modal-subtitle">{headerSubtitle}</div>
          </div>
          <button className="service-history-close modal-close-button" onClick={onClose}>×</button>
        </div>

        <div className="service-history-tabs modal-tabs">
          <button
            className={`service-history-tab modal-tab ${activeTab === 'movimentacao' ? 'active' : ''}`}
            onClick={() => setActiveTab('movimentacao')}
          >
            Movimentação
          </button>
          <button
            className={`service-history-tab modal-tab ${activeTab === 'ordens' ? 'active' : ''}`}
            onClick={() => setActiveTab('ordens')}
          >
            Ordens de serviço
          </button>
          <button
            className={`service-history-tab modal-tab ${activeTab === 'pecas' ? 'active' : ''}`}
            onClick={() => setActiveTab('pecas')}
          >
            Peças trocadas
          </button>
        </div>

        <div className="service-history-content modal-content">
          {loading && <div className="service-history-loading">Carregando...</div>}

          {!loading && activeTab === 'movimentacao' && (
            movimentacao.length === 0 ? (
              <div className="service-history-empty">Nenhum registro de movimentação.</div>
            ) : (
              <div className="movimentacao-content">
                <div className="timeline-stepper">
                  {[...movimentacao]
                    .sort((a, b) => new Date(a.TB02112_DATA) - new Date(b.TB02112_DATA))
                    .map((m, idx, sorted) => (
                      <div key={`${m.TB02112_CODIGO || 'mov'}-${idx}`} className="step-item">
                        <div className="step-marker">
                          <div className="step-dot"></div>
                          {idx < sorted.length - 1 && <div className="step-line"></div>}
                        </div>
                        <div className="step-content">
                          <div className="step-title">{m.TB01008_NOME || '-'}</div>
                          <div className="step-subtitle">{m.TB02112_CODIGO || '-'}</div>
                          <div className="step-dates">
                            <span className="step-date-label">Instalação:</span>
                            <span className="step-date-value">{m.TB02112_DATA ? new Date(m.TB02112_DATA).toLocaleDateString('pt-BR') : '-'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
                <div className="movimentacao-table-wrapper">
                  <table className="movimentacao-table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Contrato</th>
                        <th>Instalação</th>
                        <th>OS Corretivas</th>
                        <th>OS Preventivas</th>
                        <th>Desinstalação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...movimentacao]
                        .sort((a, b) => new Date(b.TB02112_DATA) - new Date(a.TB02112_DATA))
                        .map((m, idx) => (
                        <tr key={`${m.TB02112_CODIGO || 'movtbl'}-${idx}`}>
                          <td>{m.TB01008_NOME || '-'}</td>
                          <td>{m.TB02112_CODIGO || '-'}</td>
                          <td>{m.TB02112_DATA ? new Date(m.TB02112_DATA).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="td-center">{m.OS_CORRETIVAS || 0}</td>
                          <td className="td-center">{m.OS_PREVENTIVAS || 0}</td>
                          <td>{m.TB02112_DTINATIVO ? new Date(m.TB02112_DTINATIVO).toLocaleDateString('pt-BR') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}

          {!loading && activeTab === 'ordens' && (
            ordens.length === 0 ? (
              <div className="service-history-empty">Nenhuma ordem de serviço encontrada.</div>
            ) : (
              <div className="ordens-content">
                <div className="ordens-table-wrapper service-history-ordens-wrapper">
                <table className="ordens-table service-history-ordens-table">
                  <thead>
                    <tr>
                      <th>OS</th>
                      <th>
                        <div className="th-with-filter">
                          Tipo
                          <button className="filter-icon-button" onClick={() => setShowTipoFilter((v) => !v)}>
                            <ListFilter size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showTipoFilter && (
                          <div className="multiselect-filter-popup">
                            <input className="filter-search-input" value={tipoSearchText} onChange={(e) => setTipoSearchText(e.target.value)} placeholder="Buscar tipo..." />
                            <div className="filter-actions">
                              <button className="filter-action-button" onClick={() => setSelectedTipos(uniqueTipos)}>Marcar todos</button>
                              <button className="filter-action-button" onClick={() => setSelectedTipos([])}>Limpar</button>
                            </div>
                            <div className="filter-options-list">
                              {filteredTiposList.map((tipo) => (
                                <label key={tipo} className="filter-option">
                                  <input type="checkbox" checked={selectedTipos.includes(tipo)} onChange={(e) => {
                                    if (e.target.checked) setSelectedTipos((prev) => [...prev, tipo]);
                                    else setSelectedTipos((prev) => prev.filter((t) => t !== tipo));
                                  }} />
                                  <span>{tipo}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </th>
                      <th>
                        <div className="th-with-filter">
                          Abertura
                          <button className="filter-icon-button" onClick={() => setShowAberturaFilter((v) => !v)}>
                            <Calendar1 size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showAberturaFilter && (
                          <div className="date-filter-popup">
                            <input type="date" className="date-filter-input" value={aberturaStart} onChange={(e) => setAberturaStart(e.target.value)} />
                            <input type="date" className="date-filter-input" value={aberturaEnd} onChange={(e) => setAberturaEnd(e.target.value)} />
                            <button className="clear-filter-button" onClick={() => { setAberturaStart(''); setAberturaEnd(''); }}>Limpar</button>
                          </div>
                        )}
                      </th>
                      <th>
                        <div className="th-with-filter">
                          Cliente
                          <button className="filter-icon-button" onClick={() => setShowClienteFilter((v) => !v)}>
                            <ListFilter size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showClienteFilter && (
                          <div className="multiselect-filter-popup">
                            <input className="filter-search-input" value={clienteSearchText} onChange={(e) => setClienteSearchText(e.target.value)} placeholder="Buscar cliente..." />
                            <div className="filter-actions">
                              <button className="filter-action-button" onClick={() => setSelectedClientes(uniqueClientes)}>Marcar todos</button>
                              <button className="filter-action-button" onClick={() => setSelectedClientes([])}>Limpar</button>
                            </div>
                            <div className="filter-options-list">
                              {filteredClientesList.map((cliente) => (
                                <label key={cliente} className="filter-option">
                                  <input type="checkbox" checked={selectedClientes.includes(cliente)} onChange={(e) => {
                                    if (e.target.checked) setSelectedClientes((prev) => [...prev, cliente]);
                                    else setSelectedClientes((prev) => prev.filter((c) => c !== cliente));
                                  }} />
                                  <span>{cliente}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </th>
                      <th>Solicitante</th>
                      <th>Motivo</th>
                      <th>
                        <div className="th-with-filter">
                          Técnico
                          <button className="filter-icon-button" onClick={() => setShowTecnicoFilter((v) => !v)}>
                            <ListFilter size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showTecnicoFilter && (
                          <div className="multiselect-filter-popup">
                            <input className="filter-search-input" value={tecnicoSearchText} onChange={(e) => setTecnicoSearchText(e.target.value)} placeholder="Buscar técnico..." />
                            <div className="filter-actions">
                              <button className="filter-action-button" onClick={() => setSelectedTecnicos(uniqueTecnicos)}>Marcar todos</button>
                              <button className="filter-action-button" onClick={() => setSelectedTecnicos([])}>Limpar</button>
                            </div>
                            <div className="filter-options-list">
                              {filteredTecnicosList.map((tecnico) => (
                                <label key={tecnico} className="filter-option">
                                  <input type="checkbox" checked={selectedTecnicos.includes(tecnico)} onChange={(e) => {
                                    if (e.target.checked) setSelectedTecnicos((prev) => [...prev, tecnico]);
                                    else setSelectedTecnicos((prev) => prev.filter((t) => t !== tecnico));
                                  }} />
                                  <span>{tecnico}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </th>
                      <th>Condição Final</th>
                      <th>
                        <div className="th-with-filter">
                          Fechamento
                          <button className="filter-icon-button" onClick={() => setShowFechamentoFilter((v) => !v)}>
                            <Calendar1 size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showFechamentoFilter && (
                          <div className="date-filter-popup">
                            <input type="date" className="date-filter-input" value={fechamentoStart} onChange={(e) => setFechamentoStart(e.target.value)} />
                            <input type="date" className="date-filter-input" value={fechamentoEnd} onChange={(e) => setFechamentoEnd(e.target.value)} />
                            <button className="clear-filter-button" onClick={() => { setFechamentoStart(''); setFechamentoEnd(''); }}>Limpar</button>
                          </div>
                        )}
                      </th>
                      <th className="th-contador">A4 P&amp;B</th>
                      <th className="th-contador">A4 Cor</th>
                      <th className="th-contador">Dig.</th>
                      <th className="th-contador">A3 P&amp;B</th>
                      <th className="th-contador">A3 Cor</th>
                      <th>Laudo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrdens.length > 0 ? filteredOrdens.map((o, idx) => (
                      <React.Fragment key={`${o.TB02115_CODIGO || 'os'}-${idx}`}>
                        <tr>
                          <td className="td-bold">{nullableToBlank(o.TB02115_CODIGO)}</td>
                          <td className="td-center"><span className="tipo-badge">{nullableToBlank(o.TIPO_OS)}</span></td>
                          <td>{nullableToBlank(o.TB02115_DATA)}</td>
                          <td>{nullableToBlank(o.TB01008_NOME)}</td>
                          <td>{nullableToBlank(o.TB02115_SOLICITANTE)}</td>
                          <td className="td-center">
                            <button className="icon-button" onClick={() => setExpandedMotivo(expandedMotivo === idx ? null : idx)} title="Ver detalhes do motivo">ℹ️</button>
                          </td>
                          <td>{nullableToBlank(o.TB01024_NOME)}</td>
                          <td><span className="tipo-badge">{nullableToBlank(o.TB01055_NOME)}</span></td>
                          <td>{nullableToBlank(o.TB02122_DATAFIM)}</td>
                          <td className="td-center td-contador">{formatCounterCell(o.TB02122_CONTADOR)}</td>
                          <td className="td-center td-contador">{formatCounterCell(o.TB02122_CONTADORC)}</td>
                          <td className="td-center td-contador">{formatCounterCell(o.TB02122_CONTADORDG)}</td>
                          <td className="td-center td-contador">{formatCounterCell(o.TB02122_CONTADORGF)}</td>
                          <td className="td-center td-contador">{formatCounterCell(o.TB02122_CONTADORGFC)}</td>
                          <td className="td-center">
                            <button className="icon-button" onClick={() => setExpandedLaudo(expandedLaudo === idx ? null : idx)} title="Ver laudo técnico">📄</button>
                          </td>
                        </tr>
                        {expandedMotivo === idx && (
                          <tr className="expanded-row">
                            <td colSpan="15">
                              <div className="expanded-content"><strong>Motivo:</strong> {nullableToBlank(o.TB02115_NOME)}</div>
                            </td>
                          </tr>
                        )}
                        {expandedLaudo === idx && (
                          <tr className="expanded-row">
                            <td colSpan="15">
                              <div className="expanded-content"><strong>Laudo Técnico:</strong> {nullableToBlank(o.TB02122_OBS)}</div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )) : (
                      <tr>
                        <td colSpan="15" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted, #94a3b8)' }}>
                          Nenhuma ordem de serviço encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </div>
            )
          )}

          {!loading && activeTab === 'pecas' && (
            pecas.length === 0 ? (
              <div className="service-history-empty">Nenhuma peça trocada encontrada.</div>
            ) : (
              <div className="pecas-content">
              <div className="pecas-table-wrapper">
                <table className="pecas-table">
                  <thead>
                    <tr>
                      <th>Pedido</th>
                      <th>
                        <div className="th-with-filter">
                          Data
                          <button className="filter-icon-button" onClick={() => setShowPecasDataFilter((v) => !v)}>
                            <Calendar1 size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showPecasDataFilter && (
                          <div className="date-filter-popup">
                            <input type="date" className="date-filter-input" value={pecasDataStart} onChange={(e) => setPecasDataStart(e.target.value)} />
                            <input type="date" className="date-filter-input" value={pecasDataEnd} onChange={(e) => setPecasDataEnd(e.target.value)} />
                            <button className="clear-filter-button" onClick={() => { setPecasDataStart(''); setPecasDataEnd(''); }}>Limpar</button>
                          </div>
                        )}
                      </th>
                      <th>Código</th>
                      <th>
                        <div className="th-with-filter">
                          Referência
                          <button className="filter-icon-button" onClick={() => setShowReferenciaFilter((v) => !v)}>
                            <ListFilter size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showReferenciaFilter && (
                          <div className="multiselect-filter-popup">
                            <input className="filter-search-input" value={referenciaSearchText} onChange={(e) => setReferenciaSearchText(e.target.value)} placeholder="Buscar referência..." />
                            <div className="filter-actions">
                              <button className="filter-action-button" onClick={() => setSelectedReferencias(uniqueReferencias)}>Marcar todos</button>
                              <button className="filter-action-button" onClick={() => setSelectedReferencias([])}>Limpar</button>
                            </div>
                            <div className="filter-options-list">
                              {filteredReferenciasList.map((ref) => (
                                <label key={ref} className="filter-option">
                                  <input type="checkbox" checked={selectedReferencias.includes(ref)} onChange={(e) => {
                                    if (e.target.checked) setSelectedReferencias((prev) => [...prev, ref]);
                                    else setSelectedReferencias((prev) => prev.filter((r) => r !== ref));
                                  }} />
                                  <span>{ref}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </th>
                      <th>
                        <div className="th-with-filter">
                          Produto
                          <button className="filter-icon-button" onClick={() => setShowProdutoFilter((v) => !v)}>
                            <ListFilter size={14} strokeWidth={2} />
                          </button>
                        </div>
                        {showProdutoFilter && (
                          <div className="multiselect-filter-popup">
                            <input className="filter-search-input" value={produtoSearchText} onChange={(e) => setProdutoSearchText(e.target.value)} placeholder="Buscar produto..." />
                            <div className="filter-actions">
                              <button className="filter-action-button" onClick={() => setSelectedProdutos(uniqueProdutos)}>Marcar todos</button>
                              <button className="filter-action-button" onClick={() => setSelectedProdutos([])}>Limpar</button>
                            </div>
                            <div className="filter-options-list">
                              {filteredProdutosList.map((prod) => (
                                <label key={prod} className="filter-option">
                                  <input type="checkbox" checked={selectedProdutos.includes(prod)} onChange={(e) => {
                                    if (e.target.checked) setSelectedProdutos((prev) => [...prev, prod]);
                                    else setSelectedProdutos((prev) => prev.filter((p) => p !== prod));
                                  }} />
                                  <span>{prod}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </th>
                      <th>Qtde</th>
                      <th>OS</th>
                      <th>Vida útil</th>
                      <th>P&amp;B</th>
                      <th>Cor</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPecas.length > 0 ? filteredPecas.map((p, idx) => (
                      <tr key={`${p.TB02022_CODIGO || 'pc'}-${idx}`}>
                        <td>{p.TB02022_CODIGO || '-'}</td>
                        <td>{p.TB02021_DATA || '-'}</td>
                        <td>{p.TB02022_PRODUTO || '-'}</td>
                        <td>{p.TB01010_REFERENCIA || '-'}</td>
                        <td>{p.TB01010_NOME || '-'}</td>
                        <td className="td-center">{parseInt(p.TB02022_QTPROD || 0, 10)}</td>
                        <td className="td-bold">{p.TB02021_OS || '-'}</td>
                        <td>{parseInt(p.TB01010_COPIA || 0, 10).toLocaleString('pt-BR')}</td>
                        <td className="td-right">{formatCounterCell(p.TB02021_CONTPB)}</td>
                        <td className="td-right">{formatCounterCell(p.TB02021_CONTCOLOR)}</td>
                        <td className="td-right">{formatCounterCell(p.TB02021_CONTTOTAL)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted, #94a3b8)' }}>
                          Nenhuma peça trocada encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceHistoryModal;
